/**
 * BulkActionStrip — sticky-bottom action-strip chrome shared by every
 * workspace tab with bulk selection (Documenti, Conversazioni, future
 * Lavoro multi-select…).
 *
 *  ┌────────────────────────────────────────────────────────────────────┐
 *  │  N selezionati  ·  [...action slot...]                          ✕  │
 *  └────────────────────────────────────────────────────────────────────┘
 *
 * The primitive owns:
 *   - sticky-bottom positioning + glass chrome
 *   - count badge with Italian singular/plural copy
 *   - the leading hairline separator + the trailing dismiss button
 *   - ``role="toolbar"`` a11y wiring
 *
 * Each tab fills the action ``<slot>`` with its own buttons via
 * :class:`BulkActionStripButton` (compact tonal ``ghost`` button with
 * destructive variant) — keeps the vocabulary domain-specific without
 * duplicating the wrapper.
 *
 * Visible ONLY when ``count > 0``.
 */

import { X } from "lucide-react";
import type * as React from "react";

import { cn } from "../lib/cn";

import { Button } from "../primitives/button";


export interface BulkActionStripProps {
  readonly count: number;
  readonly onDeselectAll: () => void;
  /** Action buttons / popovers rendered between the count badge and
   *  the dismiss icon. Composed via :class:`BulkActionStripButton`. */
  readonly children: React.ReactNode;
  /** Accessibility name for the ``<div role="toolbar">``. */
  readonly ariaLabel: string;
  readonly className?: string;
}


export const BulkActionStrip: React.FC<BulkActionStripProps> = ({
  count,
  onDeselectAll,
  children,
  ariaLabel,
  className,
}) => {
  if (count <= 0) return null;
  return (
    <div
      role="toolbar"
      aria-label={ariaLabel}
      className={cn(
        "-translate-x-1/2 fixed bottom-6 left-1/2 z-40 flex items-center gap-2 rounded-full border border-hairline bg-white/95 px-4 py-2 shadow-lg backdrop-blur-md",
        "data-[state=open]:animate-in motion-reduce:transform-none",
        className,
      )}
      data-state="open"
    >
      <span className="font-semibold text-[12px] text-ink-1">
        <span className="tabular-nums">{count}</span>{" "}
        {count === 1 ? "selezionato" : "selezionati"}
      </span>
      <span className="mx-1 h-4 w-px bg-hairline" aria-hidden />
      {children}
      <span className="mx-1 h-4 w-px bg-hairline" aria-hidden />
      <button
        type="button"
        onClick={onDeselectAll}
        aria-label="Deseleziona tutti"
        className="flex h-6 w-6 items-center justify-center rounded-full text-ink-3 transition-colors hover:bg-black/[0.05] hover:text-ink-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
      >
        <X size={13} strokeWidth={2} aria-hidden />
      </button>
    </div>
  );
};
BulkActionStrip.displayName = "BulkActionStrip";


export interface BulkActionStripButtonProps {
  readonly icon: React.ReactNode;
  readonly label: string;
  readonly onClick?: () => void;
  /** ``destructive`` tints the label rose for "delete" / "archive". */
  readonly tone?: "default" | "destructive";
  readonly disabled?: boolean;
}


/** Compact tonal button — pair with :class:`BulkActionStrip`. */
export const BulkActionStripButton: React.FC<BulkActionStripButtonProps> = ({
  icon,
  label,
  onClick,
  tone = "default",
  disabled,
}) => (
  <Button
    type="button"
    variant="ghost"
    size="sm"
    onClick={onClick}
    disabled={disabled ?? !onClick}
    className={cn(
      "h-7 gap-1.5 px-2.5 font-medium text-[11.5px]",
      tone === "destructive"
        ? "text-rose-600 hover:bg-rose-50 hover:text-rose-700"
        : "text-ink-2 hover:bg-black/[0.05] hover:text-ink-1",
    )}
  >
    {icon}
    {label}
  </Button>
);
BulkActionStripButton.displayName = "BulkActionStripButton";
